"use client";

import { LazyMotion, MotionConfig } from "motion/react";
import type { ReactNode } from "react";

/**
 * Lazily-loaded animation feature bundle. The whole site uses the
 * lightweight `m` component (~6kb) instead of the full `motion` factory
 * (~34kb); `LazyMotion` supplies the actual DOM animation features.
 *
 * The dynamic `import()` keeps the ~15kb feature set out of the initial
 * client chunk — it streams in right after hydration, which is fine
 * because nothing animates before then. `domAnimation` (not `domMax`)
 * because the site uses no drag, layout, or shared-layout animations.
 */
const loadFeatures = () =>
  import("motion/react").then((mod) => mod.domAnimation);

/**
 * Site-wide Motion context. Three responsibilities:
 *
 * 1. `LazyMotion` with `strict` — code-splits the animation features and
 *    forces every component to use `m.*` (a stray `motion.*` throws), so
 *    the heavy `motion` factory can never sneak back into the bundle.
 * 2. `reducedMotion="user"` — honours the OS-level
 *    `prefers-reduced-motion: reduce` setting for every Motion component
 *    on the page. Components don't have to guard individually.
 * 3. A single place to set transition defaults if we ever want one.
 *
 * Mounted in `app/layout.tsx`. It's a client component but it does
 * nothing for SSR'd server children except provide context.
 */
export function MotionProvider({ children }: { children: ReactNode }) {
  return (
    <LazyMotion features={loadFeatures} strict>
      <MotionConfig reducedMotion="user">{children}</MotionConfig>
    </LazyMotion>
  );
}
